import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { API_URL } from '../config/api';
import { 
  Search, Calendar as CalendarIcon, Edit2, Trash2, TrendingUp, TrendingDown, 
  Target, Repeat, Filter, ArrowUpDown, Wallet, PiggyBank, Sparkles, X
} from 'lucide-react';
import { toast } from 'sonner';
import OperationDetailDialog from './OperationDetailDialog';
import DeleteRecurringDialog from './DeleteRecurringDialog';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const TransactionHistory = ({ calendar }) => {
  const [operations, setOperations] = useState([]);
  const [filteredOperations, setFilteredOperations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [showFilters, setShowFilters] = useState(false);
  
  // Detail dialog
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [deletingOperation, setDeletingOperation] = useState(null);

  useEffect(() => {
    loadData();
  }, [calendar]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [operations, searchText, filterType, filterCategory, filterPeriod, sortBy]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [opsResponse, catsResponse, depensesResponse] = await Promise.all([
        axios.get(`${API}/operations?calendar_id=${calendar.id}`, { withCredentials: true }),
        axios.get(`${API}/categories?calendar_id=${calendar.id}`, { withCredentials: true }),
        axios.get(`${API}/portefeuilles/${calendar.id}/depenses`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      const allOperations = [
        ...opsResponse.data,
        ...depensesResponse.data.map(depense => ({
          id: depense.id,
          date: depense.date,
          type: 'portefeuille_depense',
          amount: depense.montant,
          category: `Portefeuille : ${depense.portefeuille_nom}`,
          comment: depense.description || 'Dépense depuis portefeuille',
          account: depense.compte,
          portefeuille_depense: true,
          portefeuille_nom: depense.portefeuille_nom,
          description: depense.description
        }))
      ];
      
      setOperations(allOperations);
      setCategories(catsResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...operations];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(op => 
        op.category.toLowerCase().includes(search) ||
        (op.comment && op.comment.toLowerCase().includes(search))
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(op => op.type === filterType);
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(op => op.category === filterCategory);
    }

    if (filterPeriod !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(op => {
        const opDate = new Date(op.date + 'T00:00:00');
        
        switch (filterPeriod) {
          case 'today':
            return opDate.toDateString() === today.toDateString();
          case '7days':
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            return opDate >= sevenDaysAgo;
          case 'month':
            return opDate.getMonth() === today.getMonth() && opDate.getFullYear() === today.getFullYear();
          case 'year':
            return opDate.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'amount-desc':
          return b.amount - a.amount;
        case 'amount-asc':
          return a.amount - b.amount;
        default:
          return 0;
      }
    });

    setFilteredOperations(filtered);
  };

  const calculateSummary = () => {
    const credits = filteredOperations
      .filter(op => op.type === 'credit')
      .reduce((sum, op) => sum + op.amount, 0);
    
    const debits = filteredOperations
      .filter(op => op.type === 'debit' || op.type === 'savings')
      .reduce((sum, op) => sum + op.amount, 0);
    
    return {
      credits,
      debits,
      balance: credits - debits,
      count: filteredOperations.length
    };
  };

  const handleDelete = async (operation) => {
    if (operation.recurring_id) {
      setDeletingOperation(operation);
    } else {
      try {
        await axios.delete(`${API}/operations/${operation.id}`, { withCredentials: true });
        toast.success('Opération supprimée');
        loadData();
      } catch (error) {
        console.error('Error deleting operation:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteRecurring = async (deleteType) => {
    if (!deletingOperation) return;
    
    try {
      await axios.post(
        `${API}/operations/${deletingOperation.id}/delete-recurring`,
        { delete_type: deleteType, operation_date: deletingOperation.date },
        { withCredentials: true }
      );
      
      toast.success('Opération supprimée');
      setDeletingOperation(null);
      loadData();
    } catch (error) {
      console.error('Error deleting recurring operation:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short'
    });
  };

  const getTypeIcon = (op) => {
    if (op.portefeuille_depense) return <PiggyBank className="h-5 w-5 text-purple-600" />;
    if (op.type === 'credit') return <TrendingUp className="h-5 w-5 text-emerald-600" />;
    if (op.type === 'savings') return <Target className="h-5 w-5 text-amber-600" />;
    return <TrendingDown className="h-5 w-5 text-rose-600" />;
  };

  const getTypeColor = (op) => {
    if (op.portefeuille_depense) return 'from-purple-400 to-violet-500';
    if (op.type === 'credit') return 'from-emerald-400 to-teal-500';
    if (op.type === 'savings') return 'from-amber-400 to-orange-500';
    return 'from-rose-400 to-pink-500';
  };

  const getTypeBg = (op) => {
    if (op.portefeuille_depense) return 'bg-purple-50 border-purple-200';
    if (op.type === 'credit') return 'bg-emerald-50 border-emerald-200';
    if (op.type === 'savings') return 'bg-amber-50 border-amber-200';
    return 'bg-rose-50 border-rose-200';
  };

  const summary = calculateSummary();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <motion.div 
          className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  const hasActiveFilters = filterType !== 'all' || filterCategory !== 'all' || filterPeriod !== 'all' || searchText;

  return (
    <div className="space-y-6 p-4 sm:p-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3"
      >
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Historique des transactions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            📊 Retrouve et analyse toutes tes opérations
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className={`gap-2 ${showFilters ? 'bg-teal-50 border-teal-300 text-teal-700' : ''}`}
        >
          <Filter className="h-4 w-4" />
          Filtres
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-teal-500 rounded-full" />
          )}
        </Button>
      </motion.div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
              <Wallet className="h-4 w-4 text-gray-600" />
            </div>
            <span className="text-xs text-gray-500 font-medium">Transactions</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.count}</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
            </div>
            <span className="text-xs text-gray-500 font-medium">Entrées</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">+{summary.credits.toFixed(0)}€</p>
        </div>

        <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-4 border border-rose-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="h-4 w-4 text-rose-600" />
            </div>
            <span className="text-xs text-gray-500 font-medium">Sorties</span>
          </div>
          <p className="text-2xl font-bold text-rose-600">-{summary.debits.toFixed(0)}€</p>
        </div>

        <div className={`rounded-2xl p-4 border ${
          summary.balance >= 0 
            ? 'bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200' 
            : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              summary.balance >= 0 ? 'bg-teal-100' : 'bg-red-100'
            }`}>
              <Sparkles className={`h-4 w-4 ${summary.balance >= 0 ? 'text-teal-600' : 'text-red-600'}`} />
            </div>
            <span className="text-xs text-gray-500 font-medium">Solde</span>
          </div>
          <p className={`text-2xl font-bold ${summary.balance >= 0 ? 'text-teal-600' : 'text-red-600'}`}>
            {summary.balance >= 0 ? '+' : ''}{summary.balance.toFixed(0)}€
          </p>
        </div>
      </motion.div>

      {/* Coach Message */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className={`rounded-xl p-4 text-center ${
          summary.balance >= 0 
            ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white' 
            : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
        }`}
      >
        <p className="font-medium">
          {summary.balance >= 0 
            ? '✨ Belle gestion ! Continue comme ça, tu maîtrises ton budget.' 
            : '💡 Analyse tes dépenses pour identifier des économies possibles.'}
        </p>
      </motion.div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-white rounded-2xl p-4 border-2 border-gray-200 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Rechercher par mot-clé..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-10 border-gray-200 focus:border-teal-300"
                  data-testid="search-input"
                />
                {searchText && (
                  <button 
                    onClick={() => setSearchText('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Filter Buttons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="border-gray-200">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    <SelectItem value="credit">✅ Crédits</SelectItem>
                    <SelectItem value="debit">🔻 Débits</SelectItem>
                    <SelectItem value="savings">💰 Épargne</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="border-gray-200">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes catégories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                  <SelectTrigger className="border-gray-200">
                    <SelectValue placeholder="Période" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes périodes</SelectItem>
                    <SelectItem value="today">Aujourd'hui</SelectItem>
                    <SelectItem value="7days">7 derniers jours</SelectItem>
                    <SelectItem value="month">Ce mois</SelectItem>
                    <SelectItem value="year">Cette année</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="border-gray-200">
                    <SelectValue placeholder="Trier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date-desc">Plus récent</SelectItem>
                    <SelectItem value="date-asc">Plus ancien</SelectItem>
                    <SelectItem value="amount-desc">Montant ↓</SelectItem>
                    <SelectItem value="amount-asc">Montant ↑</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchText('');
                    setFilterType('all');
                    setFilterCategory('all');
                    setFilterPeriod('all');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Operations List */}
      <div className="space-y-3">
        {filteredOperations.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-50 rounded-2xl p-12 text-center border-2 border-dashed border-gray-200"
          >
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">Aucune transaction trouvée</p>
            <p className="text-sm text-gray-500 mt-1">Modifie tes filtres pour voir plus de résultats</p>
          </motion.div>
        ) : (
          filteredOperations.map((op, index) => (
            <motion.div
              key={op.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.2 }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className={`rounded-2xl p-4 border-2 transition-shadow hover:shadow-md ${getTypeBg(op)}`}
              data-testid={`transaction-${op.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getTypeColor(op)} flex items-center justify-center flex-shrink-0`}>
                    <div className="text-white">
                      {getTypeIcon(op)}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Category & Badges */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-900">{op.category}</span>
                      {op.recurring_id && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Repeat className="h-3 w-3" />
                          Auto
                        </span>
                      )}
                      {op.savings_goal_id && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          💰 Épargne
                        </span>
                      )}
                      {op.occurrence_number && op.total_occurrences && (
                        <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                          {op.occurrence_number}/{op.total_occurrences}
                        </span>
                      )}
                    </div>
                    
                    {/* Amount */}
                    <p className={`text-2xl font-bold ${
                      op.portefeuille_depense ? 'text-purple-600' :
                      op.type === 'credit' ? 'text-emerald-600' :
                      op.type === 'savings' ? 'text-amber-600' :
                      'text-rose-600'
                    }`}>
                      {op.type === 'credit' ? '+' : '-'}{op.amount.toFixed(2)} {calendar.currency}
                    </p>
                    
                    {/* Date & Account */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <CalendarIcon className="h-3 w-3" />
                      <span>{formatDate(op.date)}</span>
                      {op.account && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                          {op.account === 'personal' ? '👤 Perso' : '👥 Partagé'}
                        </span>
                      )}
                    </div>
                    
                    {/* Comment */}
                    {op.comment && (
                      <p className="text-sm text-gray-600 mt-2 italic bg-white/50 rounded-lg px-2 py-1">
                        "{op.comment}"
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  {!op.portefeuille_depense && !op.recurring_id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedOperation(op);
                        setShowDetailDialog(true);
                      }}
                      className="h-9 w-9 hover:bg-white"
                    >
                      <Edit2 className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                  {!op.portefeuille_depense && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(op)}
                      className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Inspirational footer */}
      {filteredOperations.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center py-6"
        >
          <div className="inline-block bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl px-6 py-4 border border-teal-200">
            <p className="text-sm text-teal-700 italic">
              "La maîtrise de tes finances, c'est la liberté de réaliser tes rêves" 🌴
            </p>
          </div>
        </motion.div>
      )}

      <OperationDetailDialog
        open={showDetailDialog}
        onClose={() => {
          setShowDetailDialog(false);
          setSelectedOperation(null);
        }}
        operation={selectedOperation}
        calendarId={calendar.id}
        currency={calendar.currency}
        onOperationChanged={loadData}
      />

      <DeleteRecurringDialog
        open={!!deletingOperation}
        onClose={() => setDeletingOperation(null)}
        onDelete={handleDeleteRecurring}
      />
    </div>
  );
};

export default TransactionHistory;
